import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

// Small dependency-free line icons (this app has no icon library) — kept as
// top-level components so they aren't recreated every render.
const iconProps = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IconBuilding = (p) => (<svg {...iconProps} {...p}><path d="M6 22V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v18" /><path d="M6 12H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h2" /><path d="M22 22V13a1 1 0 0 0-1-1h-3" /><line x1="10" y1="6" x2="14" y2="6" /><line x1="10" y1="10" x2="14" y2="10" /><line x1="10" y1="14" x2="14" y2="14" /><line x1="10" y1="18" x2="14" y2="18" /></svg>);
const IconFileText = (p) => (<svg {...iconProps} {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></svg>);
const IconList = (p) => (<svg {...iconProps} {...p}><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>);
const IconSearch = (p) => (<svg {...iconProps} {...p}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
const IconFilter = (p) => (<svg {...iconProps} {...p}><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>);
const IconDownload = (p) => (<svg {...iconProps} {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>);
const IconPlus = (p) => (<svg {...iconProps} {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);
const IconRefresh = (p) => (<svg {...iconProps} {...p}><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>);
const IconInfo = (p) => (<svg {...iconProps} {...p}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>);
const IconEdit = (p) => (<svg {...iconProps} {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>);
const IconEye = (p) => (<svg {...iconProps} {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>);
const IconHash = (p) => (<svg {...iconProps} {...p}><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></svg>);
const IconCreditCard = (p) => (<svg {...iconProps} {...p}><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>);
const IconChevronLeft = (p) => (<svg {...iconProps} {...p}><polyline points="15 18 9 12 15 6" /></svg>);
const IconChevronRight = (p) => (<svg {...iconProps} {...p}><polyline points="9 18 15 12 9 6" /></svg>);

// Client feedback: BOOKING_HOLD/BOOKING_RELEASE/PAYMENT are internal enum
// values, not something a hotel's corporate-account client should have to
// decode. Display-only relabeling — the underlying txn_type value (and the
// debit/credit-hold mechanics it drives) is untouched.
const TXN_TYPE_LABELS = {
  BOOKING_HOLD: 'Credit Reserved',
  BOOKING_RELEASE: 'Credit Released',
  PAYMENT: 'Payment Received',
  ADJUSTMENT: 'Adjustment',
  CHECKOUT_CITY_LEDGER_TRANSFER: 'Invoice Billed',
  FOLIO_ROUTE_TRANSFER: 'Invoice Billed'
};
const txnTypeLabel = (txnType) => TXN_TYPE_LABELS[txnType] || txnType;

// "Billing Only" ledger view (default) shows just what the corporate's own
// accounts team actually cares about -- what they were billed (an invoice
// transferred to their account at checkout) and what they've paid --
// matching how a hotelier explains this ledger to a client ("you were
// billed X, you paid Y, you owe X-Y"). Credit Reserved/Released rows are
// real ledger activity (temporary credit-limit holds, not charges) but
// showing them by default reads as far more account activity than actually
// happened. "All Activity" still shows every row for anyone tracing a
// credit-limit discrepancy or auditing the full history.
const BILLING_TXN_TYPES = new Set(['CHECKOUT_CITY_LEDGER_TRANSFER', 'FOLIO_ROUTE_TRANSFER', 'PAYMENT', 'ADJUSTMENT']);

const defaultCorpInvoiceConfig = {
  template: 'INV-{CORPCODE}-{SEQ}',
  seq_padding: 5,
  reset_cycle: 'NEVER',
  is_active: true,
  is_configured: false,
  current_seq: 0,
  last_reset_key: null
};

const emptyForm = {
  corporate_code: '',
  corporate_name: '',
  contact_person: '',
  mobile_number: '',
  email: '',
  gst_number: '',
  pan_number: '',
  state_code: '',
  billing_address: '',
  credit_limit: '',
  is_active: true,
  care_of: '',
  invoice_show_company_name: true,
  invoice_show_care_of: false,
  invoice_show_address: true,
  invoice_show_gst: true,
  invoice_show_guest_name: true,
  invoice_show_employee_id: false,
  invoice_show_vessel_name: false,
  invoice_show_corporate_code: true,
  invoice_show_guest_gstin: true,
  invoice_show_mobile: true,
  invoice_show_nationality: true,
  invoice_show_pax: true,
  // Default TRUE, opt-OUT via the toggle -- matches its sibling guest-level
  // fields (mobile/nationality/pax/guest GSTIN above), all of which print on
  // any invoice (corporate or not) unless a corporate explicitly hides them.
  // Corrected same-day from an opt-in default; see the matching backend
  // comment in ensureCorporateGstColumns.
  invoice_show_billing_address: true,
  invoice_field_order: []
};

// REQ-15 (docs/SRS_AMBERPARAMOUNT_REQ08-15.md) — the 7 configurable
// invoice-field toggles shown on the Corporate Master form. REQ-17 (same SRS,
// §12) adds 5 more — the print-popup's remaining previously-unconditional
// fields, now independently toggleable per corporate.
const INVOICE_FIELD_TOGGLES = [
  { key: 'invoice_show_company_name', label: 'Company Name' },
  { key: 'invoice_show_care_of', label: 'Company C/O' },
  { key: 'invoice_show_corporate_code', label: 'Corp. Code' },
  { key: 'invoice_show_address', label: 'Company Address' },
  { key: 'invoice_show_gst', label: 'Company GST' },
  { key: 'invoice_show_guest_name', label: 'Guest Name' },
  { key: 'invoice_show_guest_gstin', label: 'Guest GSTIN' },
  { key: 'invoice_show_mobile', label: 'Mobile' },
  { key: 'invoice_show_nationality', label: 'Nationality' },
  { key: 'invoice_show_pax', label: 'Pax' },
  { key: 'invoice_show_employee_id', label: 'Employee ID' },
  { key: 'invoice_show_vessel_name', label: 'Vessel Name(s)' },
  // Guest's per-booking billing address (pms_guest_master.billing_address) --
  // independent of "Company Address" above, which is the corporate's own
  // master-data address (invoice_show_address / cm.billing_address).
  { key: 'invoice_show_billing_address', label: 'Guest Billing Address' },
];

// 15-char GSTIN: 2 digit state + 10 char PAN + 1 entity + 1 'Z' + 1 check
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

function PmsCorporateMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [ledgerByCorp, setLedgerByCorp] = useState({});
  const [viewingLedgerCorp, setViewingLedgerCorp] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerViewMode, setLedgerViewMode] = useState('BILLING');
  const [paymentOpenId, setPaymentOpenId] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', reference_no: '', remarks: '' });
  const [paymentError, setPaymentError] = useState('');
  const [viewingInvoiceConfigCorp, setViewingInvoiceConfigCorp] = useState(null);
  const [invoiceConfig, setInvoiceConfig] = useState(defaultCorpInvoiceConfig);
  const [invoiceConfigLoading, setInvoiceConfigLoading] = useState(false);
  const [invoiceConfigSaving, setInvoiceConfigSaving] = useState(false);
  const [invoiceConfigContinueFrom, setInvoiceConfigContinueFrom] = useState('');
  const [invoiceConfigError, setInvoiceConfigError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const LIST_PAGE_SIZE = 10;

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadRows = async () => {
    if (!selectedProperty?.id) {
      setRows([]);
      return;
    }
    try {
      const res = await fetch('/api/v1/pms/corporates?all=1', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || 'Failed to load corporates');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load corporates');
    }
  };

  useEffect(() => {
    setError('');
    setMessage('');
    setEditingId('');
    setForm(emptyForm);
    setLedgerByCorp({});
    setPaymentOpenId(null);
    setPaymentForm({ amount: '', reference_no: '', remarks: '' });
    setPaymentError('');
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const resetForm = () => {
    setEditingId('');
    setForm(emptyForm);
  };

  // Moves the field at fromIdx to toIdx within the corporate's own invoice
  // field print order — the checkbox's on/off state is untouched. Backs both
  // the up/down buttons (fromIdx -> fromIdx±1) and drag-and-drop (fromIdx ->
  // wherever it's dropped).
  const moveFieldTo = (fromIdx, toIdx) => {
    setForm((prev) => {
      const order = prev.invoice_field_order.length ? [...prev.invoice_field_order] : INVOICE_FIELD_TOGGLES.map((t) => t.key);
      if (toIdx < 0 || toIdx >= order.length || fromIdx === toIdx) return prev;
      const [moved] = order.splice(fromIdx, 1);
      order.splice(toIdx, 0, moved);
      return { ...prev, invoice_field_order: order };
    });
  };
  const [dragFieldIdx, setDragFieldIdx] = useState(null);
  const [dragOverFieldIdx, setDragOverFieldIdx] = useState(null);

  const onSave = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!String(form.corporate_code || '').trim()) return setError('Corporate Code is required');
    if (!String(form.corporate_name || '').trim()) return setError('Corporate Name is required');
    const mobile = String(form.mobile_number || '').trim();
    if (mobile && !/^\d{10}$/.test(mobile)) return setError('Mobile number must be exactly 10 digits');
    const email = String(form.email || '').trim();
    if (email && !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,10}$/.test(email)) return setError('Enter a valid email address');
    const gstin = String(form.gst_number || '').trim().toUpperCase();
    if (gstin && !GSTIN_REGEX.test(gstin)) return setError('GST Number must be a valid 15-character GSTIN (e.g. 27ABCDE1234F1Z5)');
    const pan = String(form.pan_number || '').trim().toUpperCase();
    if (pan && !PAN_REGEX.test(pan)) return setError('PAN must be a valid 10-character PAN (e.g. ABCDE1234F)');
    const stateCode = String(form.state_code || '').trim();
    if (stateCode && !/^\d{2}$/.test(stateCode)) return setError('State Code must be 2 digits (e.g. 27 for Maharashtra)');
    setError('');
    setMessage('');
    try {
      const payload = {
        corporate_code: String(form.corporate_code || '').trim(),
        corporate_name: String(form.corporate_name || '').trim(),
        contact_person: String(form.contact_person || '').trim(),
        mobile_number: String(form.mobile_number || '').trim(),
        email: String(form.email || '').trim(),
        gst_number: gstin || null,
        pan_number: pan || null,
        state_code: stateCode || (gstin ? gstin.substring(0, 2) : null),
        billing_address: String(form.billing_address || '').trim() || null,
        care_of: String(form.care_of || '').trim() || null,
        invoice_show_company_name: form.invoice_show_company_name !== false,
        invoice_show_care_of: form.invoice_show_care_of === true,
        invoice_show_address: form.invoice_show_address !== false,
        invoice_show_gst: form.invoice_show_gst !== false,
        invoice_show_guest_name: form.invoice_show_guest_name !== false,
        invoice_show_employee_id: form.invoice_show_employee_id === true,
        invoice_show_vessel_name: form.invoice_show_vessel_name === true,
        invoice_show_corporate_code: form.invoice_show_corporate_code !== false,
        invoice_show_guest_gstin: form.invoice_show_guest_gstin !== false,
        invoice_show_mobile: form.invoice_show_mobile !== false,
        invoice_show_nationality: form.invoice_show_nationality !== false,
        invoice_show_pax: form.invoice_show_pax !== false,
        invoice_show_billing_address: form.invoice_show_billing_address !== false,
        invoice_field_order: Array.isArray(form.invoice_field_order) && form.invoice_field_order.length
          ? form.invoice_field_order.join(',')
          : null,
        credit_limit: Number(form.credit_limit || 0),
        is_active: form.is_active !== false
      };
      const url = editingId ? `/api/v1/pms/corporates/${editingId}` : '/api/v1/pms/corporates';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save corporate');
      setMessage(`Corporate ${editingId ? 'updated' : 'created'} successfully`);
      // Editing an existing corporate: repopulate the form from the server's
      // own fresh response (not a blank resetForm()) so what's shown is
      // provably what was just persisted — this was the source of "order not
      // showing correctly after update": the form reset to blank/default,
      // which reads identically to the reorder having been lost. Creating a
      // brand-new corporate still clears the form for "add another".
      if (editingId) {
        onEdit(data);
      } else {
        resetForm();
      }
      loadRows();
    } catch (err) {
      setError(err?.message || 'Failed to save corporate');
    }
  };

  const onEdit = (row) => {
    setEditingId(String(row.corporate_id));
    setForm({
      corporate_code: row.corporate_code || '',
      corporate_name: row.corporate_name || '',
      contact_person: row.contact_person || '',
      mobile_number: row.mobile_number || '',
      email: row.email || '',
      gst_number: row.gst_number || '',
      pan_number: row.pan_number || '',
      state_code: row.state_code || '',
      billing_address: row.billing_address || '',
      care_of: row.care_of || '',
      invoice_show_company_name: row.invoice_show_company_name !== false,
      invoice_show_care_of: row.invoice_show_care_of === true,
      invoice_show_address: row.invoice_show_address !== false,
      invoice_show_gst: row.invoice_show_gst !== false,
      invoice_show_guest_name: row.invoice_show_guest_name !== false,
      invoice_show_employee_id: row.invoice_show_employee_id === true,
      invoice_show_vessel_name: row.invoice_show_vessel_name === true,
      invoice_show_corporate_code: row.invoice_show_corporate_code !== false,
      invoice_show_guest_gstin: row.invoice_show_guest_gstin !== false,
      invoice_show_mobile: row.invoice_show_mobile !== false,
      invoice_show_nationality: row.invoice_show_nationality !== false,
      invoice_show_pax: row.invoice_show_pax !== false,
      invoice_show_billing_address: row.invoice_show_billing_address !== false,
      invoice_field_order: row.invoice_field_order
        ? row.invoice_field_order.split(',')
        : INVOICE_FIELD_TOGGLES.map((t) => t.key),
      credit_limit: Number(row.credit_limit || 0),
      is_active: row.is_active !== false
    });
    setMessage('');
    setError('');
    scrollToForm();
  };

  const openPayment = (corporateId) => {
    setPaymentError('');
    setPaymentForm({ amount: '', reference_no: '', remarks: '' });
    setPaymentOpenId((prev) => (prev === String(corporateId) ? null : String(corporateId)));
  };

  const submitPayment = async (corporateId) => {
    setPaymentError('');
    const amount = Number(paymentForm.amount || 0);
    if (!amount || amount <= 0) {
      setPaymentError('Enter a valid payment amount greater than zero');
      return;
    }
    try {
      const res = await fetch(`/api/v1/pms/corporates/${corporateId}/payments`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          amount,
          reference_no: String(paymentForm.reference_no || '').trim(),
          remarks: String(paymentForm.remarks || '').trim() || 'Corporate payment received'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Payment failed');
      setMessage(`Payment of ${amount.toFixed(2)} recorded successfully`);
      setPaymentOpenId(null);
      setPaymentForm({ amount: '', reference_no: '', remarks: '' });
      // Refresh list and refresh ledger if open
      await loadRows();
      const key = String(corporateId);
      if (ledgerByCorp[key]) {
        const lRes = await fetch(`/api/v1/pms/corporates/${corporateId}/ledger`, { headers: headers() });
        const lData = await lRes.json().catch(() => ({}));
        if (lRes.ok) setLedgerByCorp((prev) => ({ ...prev, [key]: lData }));
      }
    } catch (err) {
      setPaymentError(err?.message || 'Payment failed');
    }
  };

  // Ledger opens in a large popup rather than expanding inline — refetches
  // every time it's opened so the reconciled totals (Available Credit / Net
  // Outstanding) are never shown stale.
  const openLedger = async (row) => {
    setViewingLedgerCorp(row);
    setLedgerViewMode('BILLING');
    setLedgerLoading(true);
    try {
      const res = await fetch(`/api/v1/pms/corporates/${row.corporate_id}/ledger`, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load ledger');
      setLedgerByCorp((prev) => ({ ...prev, [String(row.corporate_id)]: data }));
    } catch (err) {
      setError(err?.message || 'Failed to load ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  const closeLedgerModal = () => setViewingLedgerCorp(null);

  // Per-corporate invoice number override — a small popup, not the large
  // ledger one, since it's just a form. Mirrors the property-wide Invoice
  // Number Configuration panel on Hotel Config → PMS Billing Info exactly
  // (same fields, same "Continue From" one-time override), just scoped to
  // this one corporate's own series.
  const openInvoiceConfig = async (row) => {
    setViewingInvoiceConfigCorp(row);
    setInvoiceConfigError('');
    setInvoiceConfigContinueFrom('');
    setInvoiceConfigLoading(true);
    try {
      const res = await fetch(`/api/v1/pms/corporates/${row.corporate_id}/invoice-number-config`, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load invoice number config');
      setInvoiceConfig({
        template: data?.template || defaultCorpInvoiceConfig.template,
        seq_padding: Number(data?.seq_padding || defaultCorpInvoiceConfig.seq_padding),
        reset_cycle: data?.reset_cycle || defaultCorpInvoiceConfig.reset_cycle,
        is_active: data?.is_active !== false,
        is_configured: data?.is_configured === true,
        current_seq: Number(data?.current_seq || 0),
        last_reset_key: data?.last_reset_key || null
      });
    } catch (err) {
      setInvoiceConfigError(err?.message || 'Failed to load invoice number config');
    } finally {
      setInvoiceConfigLoading(false);
    }
  };

  const closeInvoiceConfigModal = () => setViewingInvoiceConfigCorp(null);

  const saveCorpInvoiceConfig = async () => {
    if (!viewingInvoiceConfigCorp) return;
    const tpl = String(invoiceConfig.template || '').trim();
    if (!tpl) return setInvoiceConfigError('Template is required');
    if (!tpl.toUpperCase().includes('{SEQ}')) return setInvoiceConfigError('Template must include {SEQ} token');
    const continueFromRaw = String(invoiceConfigContinueFrom || '').trim();
    if (continueFromRaw && !/^\d+$/.test(continueFromRaw)) {
      return setInvoiceConfigError('Continue From must be a whole number (e.g. 1042)');
    }
    setInvoiceConfigSaving(true);
    setInvoiceConfigError('');
    try {
      const payload = {
        template: tpl,
        seq_padding: Number(invoiceConfig.seq_padding || 0),
        reset_cycle: String(invoiceConfig.reset_cycle || 'NEVER').toUpperCase(),
        is_active: invoiceConfig.is_active !== false
      };
      if (continueFromRaw) payload.current_seq = Number(continueFromRaw);
      const res = await fetch(`/api/v1/pms/corporates/${viewingInvoiceConfigCorp.corporate_id}/invoice-number-config`, {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save invoice number config');
      setMessage(`Invoice number config saved for ${viewingInvoiceConfigCorp.corporate_code}`);
      setInvoiceConfigContinueFrom('');
      setInvoiceConfig({
        template: data.template,
        seq_padding: Number(data.seq_padding || 0),
        reset_cycle: data.reset_cycle,
        is_active: data.is_active !== false,
        is_configured: true,
        current_seq: Number(data.current_seq || 0),
        last_reset_key: data.last_reset_key || null
      });
    } catch (err) {
      setInvoiceConfigError(err?.message || 'Failed to save invoice number config');
    } finally {
      setInvoiceConfigSaving(false);
    }
  };

  const downloadLedgerCsv = (corp) => {
    const led = ledgerByCorp[String(corp.corporate_id)] || {};
    const entries = [...(led.ledger || [])].reverse();
    if (!entries.length) return;
    const cols = [
      { label: 'Sl.no',      key: (_l, idx) => idx },
      { label: 'Txn Type',   key: (l) => txnTypeLabel(l.txn_type) || '' },
      { label: 'Reservation No', key: (l) => l.reservation_no || '' },
      { label: 'Reference',  key: (l) => `${l.reference_type || ''}${l.reference_id ? ` #${l.reference_id}` : ''}` },
      { label: 'Debit',      key: (l) => Number(l.debit_amount || 0).toFixed(2) },
      { label: 'Credit',     key: (l) => Number(l.credit_amount || 0).toFixed(2) },
      { label: 'Remarks',    key: (l) => l.remarks || '' },
      { label: 'Date',       key: (l) => String(l.created_at || '').slice(0, 19).replace('T', ' ') },
    ];
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const header = cols.map(c => esc(c.label)).join(',');
    // Same 500-row cap as the on-screen table — seed the running balance from
    // the full unbounded net so it still lands correctly for a corporate with
    // more history than that (see the matching note on the table render below).
    // Billing Only walks billing rows only (billed − paid), as on screen.
    const isBilling = ledgerViewMode === 'BILLING';
    const inView = (l) => !isBilling || BILLING_TXN_TYPES.has(l.txn_type);
    const visibleNet = entries.filter(inView).reduce((s, l) => s + Number(l.debit_amount || 0) - Number(l.credit_amount || 0), 0);
    let running = Number((isBilling ? led.billing_net_outstanding : led.net_outstanding) ?? visibleNet) - visibleNet;
    let shownCount = 0;
    const rows = [];
    entries.forEach((l) => {
      if (!inView(l)) return;
      running += Number(l.debit_amount || 0) - Number(l.credit_amount || 0);
      shownCount += 1;
      rows.push([...cols.map(c => esc(c.key(l, shownCount))), esc(running.toFixed(2))].join(','));
    });
    const csv = [`${header},"Running Bal."`, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CorporateLedger_${corp.corporate_code}_${ledgerViewMode === 'BILLING' ? 'BillingOnly_' : 'AllActivity_'}${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Resets the print ORDER back to default without touching any field's
  // show/hide checkbox state.
  const resetFieldOrder = () => setForm((prev) => ({ ...prev, invoice_field_order: INVOICE_FIELD_TOGGLES.map((t) => t.key) }));

  const filteredRows = rows.filter((r) => {
    if (statusFilter === 'ACTIVE' && !r.is_active) return false;
    if (statusFilter === 'INACTIVE' && r.is_active) return false;
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const haystack = `${r.corporate_code || ''} ${r.corporate_name || ''} ${r.contact_person || ''} ${r.gst_number || ''} ${r.email || ''}`.toLowerCase();
    return haystack.includes(q);
  });
  const totalListPages = Math.max(1, Math.ceil(filteredRows.length / LIST_PAGE_SIZE));
  const pageStart = (currentPage - 1) * LIST_PAGE_SIZE;
  const pageRows = filteredRows.slice(pageStart, pageStart + LIST_PAGE_SIZE);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter]);

  const downloadCorporateListCsv = () => {
    if (!filteredRows.length) return;
    const cols = [
      { label: 'Code',         key: (r) => r.corporate_code || '' },
      { label: 'Name',         key: (r) => r.corporate_name || '' },
      { label: 'Contact',      key: (r) => r.contact_person || r.mobile_number || '' },
      { label: 'GSTIN',        key: (r) => r.gst_number || '' },
      { label: 'Credit Limit', key: (r) => Number(r.credit_limit || 0).toFixed(2) },
      { label: 'Credit Used',  key: (r) => Number(r.credit_used || 0).toFixed(2) },
      { label: 'Available',    key: (r) => Number(r.available_credit || 0).toFixed(2) },
      { label: 'Status',       key: (r) => (r.is_active ? 'ACTIVE' : 'INACTIVE') },
    ];
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const header = cols.map((c) => esc(c.label)).join(',');
    const csvRows = filteredRows.map((r) => cols.map((c) => esc(c.key(r))).join(','));
    const csv = [header, ...csvRows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CorporateList_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const actionBtnStyle = (bg, color = '#fff') => ({
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
    background: bg, color, border: 'none', borderRadius: '6px',
    padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  });

  const cardStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '1.25rem', marginBottom: '1rem' };
  const iconBadgeStyle = (bg, color) => ({ width: 38, height: 38, borderRadius: '9px', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 });
  const fieldLabelStyle = { fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem', display: 'block' };

  return (
    <div style={{ padding: '1.25rem', maxWidth: '1400px', margin: '0 auto' }}>
      {error ? <div className="error-banner" style={{ marginBottom: '0.8rem' }}>{error}</div> : null}
      {message ? <div className="success-banner" style={{ marginBottom: '0.8rem' }}>{message}</div> : null}

      <div ref={formRef} style={{ ...cardStyle, ...editHighlightStyle(editHighlight) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1.1rem' }}>
          <div style={iconBadgeStyle('#eff6ff', '#2563eb')}><IconBuilding width={20} height={20} /></div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1f2937' }}>Corporate Configuration</div>
            <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>Manage your corporate details, invoice fields and company settings.</div>
          </div>
        </div>

        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1f2937', marginBottom: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <IconBuilding width={15} height={15} color="#2563eb" /> Company Information
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: '0.9rem' }}>
          <label>
            <span style={fieldLabelStyle}>Corporate Code <span style={{ color: '#dc2626' }}>*</span></span>
            <input placeholder="Enter corporate code" value={form.corporate_code} onChange={(e) => setForm({ ...form, corporate_code: e.target.value })} style={{ width: '100%' }} />
          </label>
          <label>
            <span style={fieldLabelStyle}>Corporate Name <span style={{ color: '#dc2626' }}>*</span></span>
            <input placeholder="Enter corporate name" value={form.corporate_name} onChange={(e) => setForm({ ...form, corporate_name: e.target.value })} style={{ width: '100%' }} />
          </label>
          <label>
            <span style={fieldLabelStyle}>Contact Person</span>
            <input placeholder="Enter contact person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} style={{ width: '100%' }} />
          </label>
          <label>
            <span style={fieldLabelStyle}>Mobile (10 digits)</span>
            <input type="tel" inputMode="numeric" maxLength={10} placeholder="Enter mobile number" value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value.replace(/\D/g, '').slice(0, 10) })} style={{ width: '100%' }} />
          </label>
          <label>
            <span style={fieldLabelStyle}>Email</span>
            <input type="email" placeholder="Enter email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={{ width: '100%' }} />
          </label>
          <label>
            <span style={fieldLabelStyle}>GSTIN (15 chars)</span>
            <input
              type="text"
              placeholder="Enter GSTIN"
              maxLength={15}
              value={form.gst_number}
              onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })}
              title="15-character GSTIN e.g. 27ABCDE1234F1Z5"
              style={{ width: '100%' }}
            />
          </label>
          <label>
            <span style={fieldLabelStyle}>PAN (10 chars)</span>
            <input
              type="text"
              placeholder="Enter PAN"
              maxLength={10}
              value={form.pan_number}
              onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase() })}
              title="10-character PAN e.g. ABCDE1234F"
              style={{ width: '100%' }}
            />
          </label>
          <label>
            <span style={fieldLabelStyle}>State Code (2 digit)</span>
            <input
              type="text"
              placeholder="Enter state code"
              maxLength={2}
              value={form.state_code}
              onChange={(e) => setForm({ ...form, state_code: e.target.value.replace(/\D/g, '').slice(0, 2) })}
              title="2-digit state code (auto-filled from GSTIN). 27=MH, 33=TN, 29=KA..."
              style={{ width: '100%' }}
            />
          </label>
          <label>
            <span style={fieldLabelStyle}>Billing Address</span>
            <input
              type="text"
              placeholder="Enter billing address"
              value={form.billing_address}
              onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
              style={{ width: '100%' }}
            />
          </label>
          <label>
            <span style={fieldLabelStyle}>Credit Limit</span>
            <input type="text" inputMode="decimal" placeholder="Enter credit limit" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} style={{ width: '100%' }} />
          </label>
          <label>
            <span style={fieldLabelStyle}>Status</span>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '0.65rem', top: '50%', transform: 'translateY(-50%)', width: 8, height: 8, borderRadius: '50%', background: form.is_active ? '#16a34a' : '#dc2626' }} />
              <select value={form.is_active ? 'Y' : 'N'} onChange={(e) => setForm({ ...form, is_active: e.target.value === 'Y' })} style={{ width: '100%', paddingLeft: '1.6rem' }}>
                <option value="Y">Active</option>
                <option value="N">Inactive</option>
              </select>
            </div>
          </label>
        </div>

        {/* REQ-15 (docs/SRS_AMBERPARAMOUNT_REQ08-15.md) — which fields this
            corporate wants on ITS invoices, and in what order. Different
            corporates can show different subsets in different sequences. */}
        <div style={{ marginTop: '1.3rem', paddingTop: '1.1rem', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.2rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <IconFileText width={15} height={15} color="#4f46e5" /> Invoice Field Configuration
              <span title="Controls which fields print on this corporate's invoices, and in what order." style={{ display: 'inline-flex', color: '#9ca3af', cursor: 'help' }}><IconInfo width={14} height={14} /></span>
            </div>
            <button
              type="button"
              onClick={resetFieldOrder}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, color: '#374151', cursor: 'pointer' }}
            >
              <IconRefresh width={13} height={13} /> Reset Order
            </button>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.7rem' }}>
            Drag a row (or use the arrows) to set the print order. Uncheck a field to hide it from the invoice.
          </div>
          <label style={{ display: 'block', marginBottom: '0.8rem', maxWidth: '360px' }}>
            <span style={fieldLabelStyle}>Company C/O</span>
            <input
              type="text"
              placeholder="Enter care-of line (optional)"
              value={form.care_of}
              onChange={(e) => setForm({ ...form, care_of: e.target.value })}
              style={{ width: '100%' }}
            />
          </label>
          {(() => {
            const orderedKeys = form.invoice_field_order.length ? form.invoice_field_order : INVOICE_FIELD_TOGGLES.map((t) => t.key);
            const fieldRows = orderedKeys.map((key, idx, arr) => {
              const t = INVOICE_FIELD_TOGGLES.find((x) => x.key === key);
              if (!t) return null;
              const enabled = form[t.key] === true;
              const isDragging = dragFieldIdx === idx;
              const isDropTarget = dragOverFieldIdx === idx && dragFieldIdx !== null && dragFieldIdx !== idx;
              return (
                <div
                  key={key}
                  draggable
                  onDragStart={() => setDragFieldIdx(idx)}
                  onDragOver={(e) => { e.preventDefault(); if (dragOverFieldIdx !== idx) setDragOverFieldIdx(idx); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragFieldIdx !== null) moveFieldTo(dragFieldIdx, idx);
                    setDragFieldIdx(null);
                    setDragOverFieldIdx(null);
                  }}
                  onDragEnd={() => { setDragFieldIdx(null); setDragOverFieldIdx(null); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.55rem',
                    padding: '0.5rem 0.7rem',
                    background: enabled ? '#fff' : '#f8fafc',
                    border: isDropTarget ? '1px dashed #4f46e5' : '1px solid #e2e8f0',
                    borderRadius: 8,
                    boxShadow: isDragging ? 'none' : '0 1px 2px rgba(15,44,77,0.05)',
                    opacity: isDragging ? 0.4 : 1,
                    transition: 'opacity 0.12s, border-color 0.12s',
                    cursor: 'grab',
                  }}
                >
                  <span style={{ color: '#a0aec0', fontSize: '0.9rem', lineHeight: 1, userSelect: 'none' }}>⠿</span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    background: enabled ? '#eef2ff' : '#edf2f7', color: enabled ? '#4f46e5' : '#a0aec0',
                    fontSize: '0.68rem', fontWeight: 700,
                  }}>
                    {idx + 1}
                  </span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, fontSize: '0.85rem', color: enabled ? '#1a202c' : '#a0aec0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => setForm({ ...form, [t.key]: e.target.checked })}
                    />
                    {t.label}
                  </label>
                  <div style={{ display: 'flex', gap: 3 }}>
                    <button type="button" title="Move up" disabled={idx === 0} onClick={() => moveFieldTo(idx, idx - 1)}
                      style={{
                        width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '50%', border: '1px solid #e2e8f0', background: '#fff', fontSize: '0.7rem', padding: 0,
                        color: idx === 0 ? '#cbd5e0' : '#4a5568', cursor: idx === 0 ? 'default' : 'pointer',
                      }}>▲</button>
                    <button type="button" title="Move down" disabled={idx === arr.length - 1} onClick={() => moveFieldTo(idx, idx + 1)}
                      style={{
                        width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '50%', border: '1px solid #e2e8f0', background: '#fff', fontSize: '0.7rem', padding: 0,
                        color: idx === arr.length - 1 ? '#cbd5e0' : '#4a5568', cursor: idx === arr.length - 1 ? 'default' : 'pointer',
                      }}>▼</button>
                  </div>
                </div>
              );
            });
            const half = Math.ceil(fieldRows.length / 2);
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0.9rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>{fieldRows.slice(0, half)}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>{fieldRows.slice(half)}</div>
              </div>
            );
          })()}
        </div>

        <div style={{ marginTop: '1.1rem', display: 'flex', gap: '0.6rem' }}>
          <button
            onClick={onSave}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '7px', padding: '0.55rem 1.1rem', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}
          >
            <IconPlus width={15} height={15} /> {editingId ? 'Update Corporate' : 'Add Corporate'}
          </button>
          <button
            onClick={resetForm}
            style={{ background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: '7px', padding: '0.55rem 1.1rem', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}
          >
            Clear
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.8rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <div style={iconBadgeStyle('#eff6ff', '#2563eb')}><IconList width={20} height={20} /></div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1f2937' }}>Corporate List</div>
              <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>Manage all corporate accounts and their details.</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', display: 'flex' }}><IconSearch width={14} height={14} /></span>
              <input
                placeholder="Search corporate..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '2rem', width: '220px' }}
              />
            </div>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setFilterOpen((v) => !v)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.4rem 0.7rem', fontSize: '0.82rem', fontWeight: 600, color: '#374151', cursor: 'pointer' }}
              >
                <IconFilter width={14} height={14} /> Filter
              </button>
              {filterOpen ? (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 6px 20px rgba(0,0,0,0.12)', padding: '0.6rem', zIndex: 10, minWidth: '160px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', marginBottom: '0.4rem' }}>STATUS</div>
                  {['ALL', 'ACTIVE', 'INACTIVE'].map((opt) => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0', fontSize: '0.82rem', color: '#374151', cursor: 'pointer' }}>
                      <input type="radio" name="corp-status-filter" checked={statusFilter === opt} onChange={() => { setStatusFilter(opt); setFilterOpen(false); }} />
                      {opt === 'ALL' ? 'All' : opt === 'ACTIVE' ? 'Active' : 'Inactive'}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={downloadCorporateListCsv}
              disabled={!filteredRows.length}
              title="Download CSV"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', color: '#374151', cursor: filteredRows.length ? 'pointer' : 'not-allowed', opacity: filteredRows.length ? 1 : 0.5 }}
            >
              <IconDownload width={15} height={15} />
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              {/* position: sticky on each <th> individually (not <thead>) —
                  the reliable cross-browser pattern for a border-collapse
                  table; see the matching note on the ledger table below. */}
              <tr>
                {['#', 'Code', 'Name', 'Contact', 'GSTIN'].map((h) => (
                  <th key={h} style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f8fafc', color: '#374151', textTransform: 'uppercase', fontSize: '0.72rem', padding: '0.6rem 0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
                {['Credit Limit', 'Credit Used', 'Available'].map((h) => (
                  <th key={h} style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f8fafc', color: '#374151', textTransform: 'uppercase', fontSize: '0.72rem', padding: '0.6rem 0.5rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
                {['Status', 'Actions'].map((h) => (
                  <th key={h} style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f8fafc', color: '#374151', textTransform: 'uppercase', fontSize: '0.72rem', padding: '0.6rem 0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r, rowIdx) => (
                <React.Fragment key={r.corporate_id}>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.6rem 0.5rem', color: '#9ca3af' }}>{pageStart + rowIdx + 1}</td>
                    <td style={{ padding: '0.6rem 0.5rem', fontWeight: 600, color: '#1f2937' }}>{r.corporate_code}</td>
                    <td style={{ padding: '0.6rem 0.5rem' }}>{r.corporate_name}</td>
                    <td style={{ padding: '0.6rem 0.5rem' }}>{r.contact_person || r.mobile_number || '-'}</td>
                    <td style={{ padding: '0.6rem 0.5rem', fontFamily: 'monospace', fontSize: '0.82rem', color: r.gst_number ? '#1d4ed8' : '#a0aec0' }}>
                      {r.gst_number || '— not set —'}
                    </td>
                    <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right' }}>{Number(r.credit_limit || 0).toFixed(2)}</td>
                    <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', color: Number(r.credit_used || 0) > 0 ? '#c05621' : '#1a202c' }}>{Number(r.credit_used || 0).toFixed(2)}</td>
                    <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', color: Number(r.available_credit || 0) <= 0 ? '#c53030' : '#2f855a' }}>{Number(r.available_credit || 0).toFixed(2)}</td>
                    <td style={{ padding: '0.6rem 0.5rem' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.15rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, background: r.is_active ? '#dcfce7' : '#fee2e2', color: r.is_active ? '#16a34a' : '#dc2626' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: r.is_active ? '#16a34a' : '#dc2626' }} />
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem 0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                        <button onClick={() => onEdit(r)} style={actionBtnStyle('#2563eb')}><IconEdit width={12} height={12} /> Edit</button>
                        <button onClick={() => openLedger(r)} style={actionBtnStyle('#1e3a5f')}><IconEye width={12} height={12} /> View Ledger</button>
                        <button onClick={() => openInvoiceConfig(r)} style={actionBtnStyle('#7c3aed')}><IconHash width={12} height={12} /> Invoice #</button>
                        {Number(r.credit_used || 0) > 0 ? (
                          <button onClick={() => openPayment(r.corporate_id)} style={actionBtnStyle(paymentOpenId === String(r.corporate_id) ? '#c05621' : '#16a34a')}>
                            <IconCreditCard width={12} height={12} /> {paymentOpenId === String(r.corporate_id) ? 'Cancel Payment' : 'Record Payment'}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  {paymentOpenId === String(r.corporate_id) ? (
                    <tr>
                      <td colSpan={10} style={{ padding: '0.6rem 0.7rem', background: '#f0fff4', borderBottom: '2px solid #9ae6b4' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#276749', marginBottom: '0.45rem' }}>
                          Record Payment — {r.corporate_code} | {r.corporate_name}
                        </div>
                        <div style={{ fontSize: '0.82rem', color: '#2d3748', marginBottom: '0.45rem' }}>
                          Outstanding Credit Used: <strong style={{ color: '#c05621' }}>{Number(r.credit_used || 0).toFixed(2)}</strong>
                          &nbsp;&nbsp;|&nbsp;&nbsp;
                          Available Credit: <strong style={{ color: Number(r.available_credit || 0) <= 0 ? '#c53030' : '#276749' }}>{Number(r.available_credit || 0).toFixed(2)}</strong>
                        </div>
                        {paymentError ? <div style={{ color: '#c53030', marginBottom: '0.4rem', fontSize: '0.82rem' }}>{paymentError}</div> : null}
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Amount *</div>
                            <input
                              type="number"
                              min={0.01}
                              step="0.01"
                              max={Number(r.credit_used || 0)}
                              placeholder={`Max ${Number(r.credit_used || 0).toFixed(2)}`}
                              value={paymentForm.amount}
                              onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
                              style={{ width: '140px' }}
                            />
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Reference No (Cheque / NEFT / UTR)</div>
                            <input
                              type="text"
                              placeholder="e.g. NEFT123456"
                              value={paymentForm.reference_no}
                              onChange={(e) => setPaymentForm((p) => ({ ...p, reference_no: e.target.value }))}
                              style={{ width: '190px' }}
                            />
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Remarks</div>
                            <input
                              type="text"
                              placeholder="Payment remarks"
                              value={paymentForm.remarks}
                              onChange={(e) => setPaymentForm((p) => ({ ...p, remarks: e.target.value }))}
                              style={{ width: '210px' }}
                            />
                          </div>
                          <button
                            onClick={() => submitPayment(r.corporate_id)}
                            className="btn-success"
                            style={{ borderRadius: '4px', padding: '0.35rem 0.9rem', cursor: 'pointer', fontWeight: 600 }}
                          >
                            Submit Payment
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))}
              {!pageRows.length ? <tr><td colSpan={10} style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af' }}>{rows.length ? 'No corporates match your search/filter' : 'No corporates found'}</td></tr> : null}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem', marginTop: '0.8rem' }}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            {filteredRows.length
              ? `Showing ${pageStart + 1} to ${Math.min(pageStart + LIST_PAGE_SIZE, filteredRows.length)} of ${filteredRows.length} entries`
              : 'Showing 0 entries'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', color: currentPage <= 1 ? '#d1d5db' : '#374151', cursor: currentPage <= 1 ? 'default' : 'pointer' }}
            >
              <IconChevronLeft width={14} height={14} />
            </button>
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '30px', height: '30px', borderRadius: '6px', background: '#2563eb', color: '#fff', fontSize: '0.82rem', fontWeight: 600, padding: '0 0.5rem' }}>
              {currentPage}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalListPages, p + 1))}
              disabled={currentPage >= totalListPages}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', color: currentPage >= totalListPages ? '#d1d5db' : '#374151', cursor: currentPage >= totalListPages ? 'default' : 'pointer' }}
            >
              <IconChevronRight width={14} height={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Ledger — large popup rather than an inline row-expansion, so a long
          history doesn't eat the whole page. Column header stays fixed while
          only the ledger rows scroll (flex layout: title/summary/download
          never scroll, only the table body area does; the table's own thead
          is sticky within that area too, for the same effect on wide+short
          viewports where the outer scroll area itself doesn't kick in). */}
      {viewingLedgerCorp ? (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeLedgerModal(); }}
        >
          <div style={{ width: 'min(1100px, 96vw)', maxHeight: '88vh', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <button
              onClick={closeLedgerModal}
              style={{ position: 'absolute', top: '0.6rem', right: '0.7rem', border: '1px solid #cbd5e0', background: '#fff', color: '#2d3748', borderRadius: '4px', padding: '0.1rem 0.5rem', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1 }}
            >
              ×
            </button>

            <div style={{ padding: '1rem 1.2rem 0.6rem', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '0.5rem', paddingRight: '1.8rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--theme-primary)' }}>
                  Corporate Ledger — {viewingLedgerCorp.corporate_code} &nbsp;|&nbsp; {viewingLedgerCorp.corporate_name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#4a5568' }}>
                    {ledgerLoading ? 'Loading...' : (() => {
                      const all = ledgerByCorp[String(viewingLedgerCorp.corporate_id)]?.ledger || [];
                      const shown = ledgerViewMode === 'BILLING' ? all.filter((l) => BILLING_TXN_TYPES.has(l.txn_type)) : all;
                      return shown.length === all.length ? `${all.length} entries` : `${shown.length} of ${all.length} entries`;
                    })()}
                  </div>
                  <button
                    onClick={() => downloadLedgerCsv(viewingLedgerCorp)}
                    disabled={!(ledgerByCorp[String(viewingLedgerCorp.corporate_id)]?.ledger || []).length}
                    style={{ padding: '0.3rem 0.7rem', borderRadius: '5px', border: '1px solid #48bb78', background: '#f0fff4', color: '#276749', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                  >
                    ⬇ Download CSV
                  </button>
                </div>
              </div>

              {/* Billing Only (default) shows just what a corporate's accounts
                  team cares about -- invoices billed and payments received --
                  matching "you were billed X, paid Y, owe X-Y". All Activity
                  reveals the Credit Reserved/Released rows too, for tracing a
                  credit-limit discrepancy. See BILLING_TXN_TYPES above. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 600 }}>View:</span>
                <button
                  onClick={() => setLedgerViewMode('BILLING')}
                  style={{
                    padding: '0.25rem 0.7rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    border: ledgerViewMode === 'BILLING' ? '1px solid #2b6cb0' : '1px solid #cbd5e0',
                    background: ledgerViewMode === 'BILLING' ? '#2b6cb0' : '#fff',
                    color: ledgerViewMode === 'BILLING' ? '#fff' : '#4a5568'
                  }}
                >
                  Billing Only
                </button>
                <button
                  onClick={() => setLedgerViewMode('ALL')}
                  style={{
                    padding: '0.25rem 0.7rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    border: ledgerViewMode === 'ALL' ? '1px solid #2b6cb0' : '1px solid #cbd5e0',
                    background: ledgerViewMode === 'ALL' ? '#2b6cb0' : '#fff',
                    color: ledgerViewMode === 'ALL' ? '#fff' : '#4a5568'
                  }}
                >
                  All Activity
                </button>
                <span style={{ fontSize: '0.72rem', color: '#a0aec0' }}>
                  {ledgerViewMode === 'BILLING' ? '— invoices billed & payments received only' : '— includes credit-hold activity'}
                </span>
              </div>

              {/* Summary bar — all 4 money figures come from the ledger
                  endpoint's own reconciled totals (SUM over the FULL ledger,
                  not just the entries on screen), not from the separately-
                  maintained credit_used counter on the corporate row — so
                  Available Credit always agrees with Credit Limit minus Net
                  Outstanding shown here. */}
              {(() => {
                const led = ledgerByCorp[String(viewingLedgerCorp.corporate_id)] || {};
                const totalDebit  = Number(led.ledger_total_debit || 0);
                const totalCredit = Number(led.ledger_total_credit || 0);
                // Total Credit includes both real PAYMENT rows and
                // BOOKING_RELEASE rows (a credit-hold being cleared, never
                // actual money) — paymentsReceived isolates just the real
                // payments, since labeling the combined figure "(Paid)" was
                // client feedback that it read as if the whole amount had
                // been paid, when most of it can just be released holds.
                const paymentsReceived = Number(led.payments_received || 0);
                // Billing Only: invoices billed − payments, with any active
                // credit hold shown on its own so Limit − Net − On Hold =
                // Available still adds up. All Activity keeps raw totals.
                const isBilling = ledgerViewMode === 'BILLING';
                const fullNet = Number(led.net_outstanding ?? (totalDebit - totalCredit));
                const net = isBilling ? Number(led.billing_net_outstanding ?? fullNet) : fullNet;
                const onHold = Number(led.credit_on_hold || 0);
                const available = Number(led.available_credit ?? viewingLedgerCorp.available_credit ?? 0);
                return (
                  <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap' }}>
                    <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Credit Limit</div>
                      <div style={{ fontWeight: 700, color: '#2d3748' }}>{Number(viewingLedgerCorp.credit_limit || 0).toFixed(2)}</div>
                    </div>
                    {isBilling ? (
                      <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Total Billed</div>
                        <div style={{ fontWeight: 700, color: '#c05621' }}>{Number(led.billing_total_debit ?? totalDebit).toFixed(2)}</div>
                      </div>
                    ) : (<>
                      <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Total Debit (Used)</div>
                        <div style={{ fontWeight: 700, color: '#c05621' }}>{totalDebit.toFixed(2)}</div>
                      </div>
                      <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Total Credit</div>
                        <div style={{ fontWeight: 700, color: '#276749' }}>{totalCredit.toFixed(2)}</div>
                      </div>
                    </>)}
                    <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#276749', textTransform: 'uppercase' }}>Payments Received</div>
                      <div style={{ fontWeight: 700, color: '#276749' }}>{paymentsReceived.toFixed(2)}</div>
                    </div>
                    <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Net Outstanding</div>
                      <div style={{ fontWeight: 700, color: net > 0 ? '#c53030' : '#276749' }}>{net.toFixed(2)}</div>
                    </div>
                    {isBilling && Math.abs(onHold) > 0.009 && (
                      <div title="Credit reserved for bookings not yet checked out" style={{ background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#975a16', textTransform: 'uppercase' }}>On Hold (Bookings)</div>
                        <div style={{ fontWeight: 700, color: '#975a16' }}>{onHold.toFixed(2)}</div>
                      </div>
                    )}
                    <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Available Credit</div>
                      <div style={{ fontWeight: 700, color: available <= 0 ? '#c53030' : '#276749' }}>{available.toFixed(2)}</div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Ledger table — this is the part that actually scrolls; the
                header block above never moves, and the table's own thead is
                sticky (top: 0) within this area so the column labels stay
                fixed even as rows scroll underneath them. */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto', borderTop: '1px solid #e2e8f0', margin: '0 1.2rem 1.2rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.82rem' }}>
                <thead>
                  {/* position: sticky on <thead> itself is unreliable with
                      border-collapse tables across browsers — putting it on
                      each <th> individually (with its own opaque background,
                      since a stuck cell needs to paint over rows scrolling
                      beneath it) is the reliable cross-browser pattern. */}
                  <tr>
                    <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>#</th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Txn Type</th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Reference</th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Debit</th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Credit</th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Running Bal.</th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Remarks</th>
                    <th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const led = ledgerByCorp[String(viewingLedgerCorp.corporate_id)] || {};
                    const entries = [...(led.ledger || [])].reverse();
                    // The ledger endpoint caps at the 500 most recent rows — for a
                    // corporate with more history than that, starting the running
                    // balance at 0 here would be wrong (it'd omit everything older).
                    // Seed it from the full unbounded total minus what's actually
                    // visible, so the running balance always lands on net_outstanding
                    // by the last row, matching the summary tile above.
                    // Billing Only: the balance walks only billing rows (billed −
                    // paid), so a credit hold never shows up in Running Bal.
                    // All Activity: every row, landing on net_outstanding.
                    const isBilling = ledgerViewMode === 'BILLING';
                    const inView = (l) => !isBilling || BILLING_TXN_TYPES.has(l.txn_type);
                    const visibleNet = entries.filter(inView).reduce((s, l) => s + Number(l.debit_amount || 0) - Number(l.credit_amount || 0), 0);
                    const fullNet = Number((isBilling ? led.billing_net_outstanding : led.net_outstanding) ?? visibleNet);
                    let running = fullNet - visibleNet;
                    if (!entries.length) {
                      return (
                        <tr><td colSpan={8} style={{ padding: '0.6rem 0.5rem', color: '#718096' }}>{ledgerLoading ? 'Loading...' : 'No ledger entries found for this corporate.'}</td></tr>
                      );
                    }
                    let shownIdx = 0;
                    const renderedRows = [];
                    entries.forEach((l) => {
                      if (!inView(l)) return;
                      running += Number(l.debit_amount || 0) - Number(l.credit_amount || 0);
                      shownIdx += 1;
                      renderedRows.push(
                        <tr key={l.ledger_id} style={{ borderBottom: '1px solid #edf2f7', background: shownIdx % 2 === 1 ? '#fff' : '#f7fafc' }}>
                          <td style={{ padding: '0.32rem 0.5rem', color: '#718096' }}>{shownIdx}</td>
                          <td style={{ padding: '0.32rem 0.5rem', fontWeight: 500 }}>{txnTypeLabel(l.txn_type)}</td>
                          <td style={{ padding: '0.32rem 0.5rem' }}>
                            {/* reservation_no (e.g. RES-00019) is the identifier front desk/
                                accounts actually recognize — reference_type/reference_id is
                                an internal row id (e.g. CITY_LEDGER_TXN #19) meaningful only
                                for tracing the exact posting, so it's kept as secondary text. */}
                            {l.reservation_no ? (
                              <div style={{ fontWeight: 600, color: '#2b6cb0' }}>{l.reservation_no}</div>
                            ) : null}
                            <div style={l.reservation_no ? { fontSize: '0.72rem', color: '#a0aec0' } : undefined}>
                              {l.reference_type || '-'}
                              {l.reference_id ? <span style={{ color: l.reservation_no ? '#a0aec0' : '#2b6cb0', marginLeft: '0.3rem' }}>#{l.reference_id}</span> : null}
                            </div>
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', textAlign: 'right', color: Number(l.debit_amount || 0) > 0 ? '#c05621' : '#a0aec0' }}>
                            {Number(l.debit_amount || 0) > 0 ? Number(l.debit_amount).toFixed(2) : '-'}
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', textAlign: 'right', color: Number(l.credit_amount || 0) > 0 ? '#276749' : '#a0aec0' }}>
                            {Number(l.credit_amount || 0) > 0 ? Number(l.credit_amount).toFixed(2) : '-'}
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', textAlign: 'right', fontWeight: 600, color: running > 0 ? '#c53030' : '#276749' }}>
                            {running.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', color: '#4a5568', minWidth: '220px', maxWidth: '360px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {l.remarks || '-'}
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', whiteSpace: 'nowrap', color: '#4a5568' }}>
                            {String(l.created_at || '').slice(0, 19).replace('T', ' ')}
                          </td>
                        </tr>
                      );
                    });
                    return renderedRows.length ? renderedRows : (
                      <tr><td colSpan={8} style={{ padding: '0.6rem 0.5rem', color: '#718096' }}>No billing activity yet for this corporate — switch to "All Activity" to see credit-hold activity.</td></tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {/* Per-corporate invoice number override — small form popup, mirroring
          the property-wide Invoice Number Configuration panel on Hotel
          Config → PMS Billing Info exactly. */}
      {viewingInvoiceConfigCorp ? (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
          onMouseDown={(e) => { if (e.target === e.currentTarget && !invoiceConfigSaving) closeInvoiceConfigModal(); }}
        >
          <div style={{ width: 'min(560px, 96vw)', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', padding: '1.1rem 1.2rem', position: 'relative' }}>
            <button
              onClick={closeInvoiceConfigModal}
              style={{ position: 'absolute', top: '0.6rem', right: '0.7rem', border: '1px solid #cbd5e0', background: '#fff', color: '#2d3748', borderRadius: '4px', padding: '0.1rem 0.5rem', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1 }}
            >
              ×
            </button>
            <h3 style={{ marginTop: 0, marginBottom: '0.2rem', color: 'var(--theme-primary)', paddingRight: '1.6rem' }}>
              Invoice Number — {viewingInvoiceConfigCorp.corporate_code} | {viewingInvoiceConfigCorp.corporate_name}
            </h3>
            <p style={{ margin: '0 0 0.7rem 0', fontSize: '0.82rem', color: '#6b7280' }}>
              {invoiceConfig.is_configured
                ? 'This corporate uses its own invoice number series below.'
                : "Not configured — this corporate's invoices still use the property's shared series. GST allows multiple series under one GSTIN as long as each stays independently gapless — configure and save below to give this corporate its own."}
            </p>
            {invoiceConfigError ? <div style={{ color: '#c53030', marginBottom: '0.6rem', fontSize: '0.85rem' }}>{invoiceConfigError}</div> : null}

            {invoiceConfigLoading ? (
              <div style={{ padding: '1rem 0', textAlign: 'center', color: '#718096' }}>Loading...</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.55rem', marginBottom: '0.6rem' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem', gridColumn: '1 / -1' }}>
                    Template
                    <input
                      value={invoiceConfig.template}
                      onChange={(e) => setInvoiceConfig((p) => ({ ...p, template: e.target.value }))}
                      placeholder="INV-{CORPCODE}-{SEQ}"
                    />
                    <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 400 }}>
                      Tokens: {'{YYYY}'} {'{YY}'} {'{MM}'} {'{DD}'} {'{FY}'} {'{SEQ}'} {'{HOTELCODE}'} {'{CORPCODE}'} &mdash; {'{SEQ}'} is required.
                    </span>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
                    Seq Padding
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={invoiceConfig.seq_padding}
                      onChange={(e) => setInvoiceConfig((p) => ({ ...p, seq_padding: e.target.value }))}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
                    Reset Cycle
                    <select value={invoiceConfig.reset_cycle} onChange={(e) => setInvoiceConfig((p) => ({ ...p, reset_cycle: e.target.value }))}>
                      <option value="NEVER">NEVER</option>
                      <option value="YEARLY">YEARLY (Jan-Dec)</option>
                      <option value="FINANCIAL_YEAR">FINANCIAL YEAR (Apr-Mar)</option>
                      <option value="MONTHLY">MONTHLY</option>
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
                    Status
                    <select value={invoiceConfig.is_active ? 'Y' : 'N'} onChange={(e) => setInvoiceConfig((p) => ({ ...p, is_active: e.target.value === 'Y' }))}>
                      <option value="Y">Active</option>
                      <option value="N">Inactive</option>
                    </select>
                  </label>
                </div>

                <div style={{ padding: '0.6rem', background: '#fffaf0', borderRadius: '6px', border: '1px dashed #f6ad55', marginBottom: '0.7rem' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
                    Continue From (optional, one-time)
                    <input
                      type="text"
                      inputMode="numeric"
                      value={invoiceConfigContinueFrom}
                      onChange={(e) => setInvoiceConfigContinueFrom(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="e.g. 1042"
                    />
                  </label>
                  <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>
                    Already numbering this corporate manually? Enter the last number used (e.g. 1042) — the next
                    invoice for this corporate will be 1043. GST requires the series to stay gapless, so double-check
                    this against the last invoice actually issued before saving.
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.82rem', color: '#4a5568' }}>
                    Current Sequence: <b>{Number(invoiceConfig.current_seq || 0)}</b>
                    {invoiceConfig.last_reset_key ? <span> | Last Reset Key: <b>{invoiceConfig.last_reset_key}</b></span> : null}
                  </div>
                  <button onClick={saveCorpInvoiceConfig} disabled={invoiceConfigSaving} style={{ minWidth: '150px' }}>
                    {invoiceConfigSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PmsCorporateMaster;
